import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Back from "./../../components/Back";
import { Stack, CircularProgress, Button } from "@mui/material";
import { Images } from "./../../assets/common";
import LeaderboardRow from "./../../components/LeaderBoardRow";
import NoData from "../../components/Nodata";
import ApiService from "../../services/api-service";
import { getUserId, hasPremium } from "../../constants/function";
import Loader from "./../../components/Loader";
import { useParams } from "react-router-dom";
import LevelCard from "../../components/LevelCard";
import { useTranslation } from "react-i18next";
import { kFormatter } from "../../constants/function";
import SubscriptionPopup from "../../components/SubscriptionPopup";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

export default function LeaderBoard() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [avgLevel, setavgLevel] = React.useState(0);
  const theme = useTheme();
  const [data, setData] = React.useState({});
  const [creatorData, setCreatorData] = React.useState({});
  const [value, setValue] = React.useState(0);
  const userId = getUserId();
  const [showSubscriptionPopup, setShowSubscriptionPopup] = React.useState(false);
  const isPremium = hasPremium();
  const handleChange = (event, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        setData({});
        setCreatorData({}); // Clear creator data when switching to curator
        getCuratorData(id ? id : userId);
        break;

      case 1:
        setData({});
        setCreatorData({}); // Clear creator data before fetching new
        getCreatorData(id ? id : userId);
        break;
    }
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const getCuratorData = async (_id) => {
    // Don't load data if user doesn't have premium
    if (!isPremium) {
      setData({});
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.time('Curator Data Load'); // Performance tracking
      
      // Try optimized v2 endpoint first (5-10x faster)
      try {
        const response = await ApiService.GAMIFICATION_CURATOR_V2(_id);
        setData({ ...response["result"] });
        setavgLevel(response["result"]["avgLevel"]);
        console.timeEnd('Curator Data Load');
        setLoading(false);
        return;
      } catch (v2Error) {
        console.log("V2 curator endpoint failed, falling back to v1:", v2Error);
        // Fallback to v1 endpoint
        const response = await ApiService.CURATOR_DATA(_id);
        setData({ ...response["result"] });
        setavgLevel(response["result"]["avgLevel"]);
        console.timeEnd('Curator Data Load');
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Both curator endpoints failed:", error);
      setLoading(false);
    }
  };
  
  const getCreatorData = async (_id) => {
    // Don't load data if user doesn't have premium
    if (!isPremium) {
      setCreatorData({});
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.time('Creator Data Load'); // Performance tracking
      
      // Try optimized complete endpoint to get all data including live stream stats
      try {
        const response = await ApiService.GAMIFICATION_COMPLETE(_id);
        const completeData = response["result"];
        
        // Set creator data with live stream stats included
        console.log('Complete gamification data:', completeData);
        console.log('Creator data:', completeData.creator);
        console.log('LiveStream data:', completeData.liveStream);
        console.log('Creator.liveStream data:', completeData.creator?.liveStream);
        
        // Ensure liveStream data is properly included
        const creatorDataToSet = {
          ...completeData.creator,
          liveStream: completeData.liveStream || completeData.creator?.liveStream || completeData.liveStreamStats,
          totalLevel: completeData.totalLevel,
          totalPoints: completeData.totalPoints,
          avgLevel: completeData.avgLevel || completeData.creator?.avgLevel
        };
        
        console.log('Setting creatorData state to:', creatorDataToSet);
        setCreatorData(creatorDataToSet);
        setavgLevel(completeData.avgLevel || completeData.creator?.avgLevel || 1);
        console.timeEnd('Creator Data Load');
        setLoading(false);
        return;
      } catch (completeError) {
        console.log("Complete endpoint failed, trying v2 creator:", completeError);
        
        // Try v2 creator endpoint
        try {
          const response = await ApiService.GAMIFICATION_CREATOR_V2(_id);
          setCreatorData({ ...response["result"] });
          setavgLevel(response["result"]["avgLevel"]);
          console.timeEnd('Creator Data Load');
          setLoading(false);
          return;
        } catch (v2Error) {
          console.log("V2 creator endpoint failed, falling back to v1:", v2Error);
          // Fallback to v1 endpoint
          const response = await ApiService.CREATOR_DATA(_id);
          setCreatorData({ ...response["result"] });
          setavgLevel(response["result"]["avgLevel"]);
          console.timeEnd('Creator Data Load');
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error("All creator endpoints failed:", error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getCuratorData(id ? id : userId);
  }, []);
  
  // Debug effect to log creatorData when it changes
  React.useEffect(() => {
    if (creatorData && Object.keys(creatorData).length > 0) {
      console.log('CreatorData state updated:', creatorData);
      console.log('LiveStream data:', creatorData.liveStream);
    }
  }, [creatorData]);
  return (
    <Box sx={{ padding: "0 22px 100px 0" }}>
      <Back />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ margin: "0 0 20px !important", padding: "0 20px" }}>
        <Typography className="web_header">
          {t("leaderBoard.title")}
        </Typography>
        {/* Global Leaderboard temporarily disabled
        <Button 
          variant="outlined" 
          size="small"
          onClick={() => window.location.href = '/app/GlobalLeaderboard'}
          sx={{ borderColor: '#2CF488', color: '#2CF488' }}
        >
          View Global Rankings
        </Button> */}
      </Stack>
      <Box
      // sx={{ backgroundColor: "#1E1E1E" }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="full width tabs example"
        >
          <Tab label={t("leaderBoard.tabs.tab1")} {...a11yProps(0)} />
          <Tab label={t("leaderBoard.tabs.tab2")} {...a11yProps(1)} />
        </Tabs>
        <Box axis={theme.direction === "rtl" ? "x-reverse" : "x"} index={value}>
          <TabPanel value={value} index={0} dir={theme.direction}>
            <Stack>
              <Box sx={{  py: [4, 3, 1], textAlign: "center", position: "relative" }}>
                <Stack>
                  <Typography
                    sx={{
                      fontSize: ["48px !important","60px !important", "96px !important"],
                      lineHeight: "1 !important",
                    }}
                  >
                    {isPremium ? (data?.overallLevel || data?.avgLevel || avgLevel || 1) : "?"}
                  </Typography>
                  <Typography
                    className="fs-20"
                    sx={{ color: "#2CF488", letterSpacing: ["12px !important", "24px !important"], }}
                  >
                    CURATOR LEVEL
                  </Typography>
                </Stack>
              </Box>
              <Box
                className="__main_pading"
                sx={{
                  paddingBottom: "100px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                }}
              >
                {isPremium ? (
                  <>
                    <Stack gap="35px" direction="row">
                  {/* {data["views"] ? (
                    <LevelCard
                      data={data["views"]}
                      width={"33%"}
                      title={"Views"}
                      color="#62FFE1"
                      icon={Images.show}
                      // path="/app/LifetimeCount/Views"
                    />
                  ) : null} */}
                  {/* {data["likes"] ? (
                    <LevelCard
                      data={data["likes"]}
                      width={"25%"}
                      title={"Likes"}
                      color="#FFEE20"
                      icon={Images.likes}
                      // path="/app/LifetimeCount/Likes"
                    />
                  ) : null} */}
                  {data["tips"] ? (
                    <LevelCard
                      isAmount={true}
                      data={data["tips"]}
                      width={"50%"}
                      title="Tips Given"
                      color="#F48706"
                      icon="/images/hand-holding-heart-solid.svg"
                      path="/app/LifetimeCount/TipsGiven/curator"
                    />
                  ) : null}
                </Stack>
                <Stack gap="35px" direction="row">
                  {data["listens"] ? (
                    <LevelCard
                      // isAmount={true}
                      data={data["listens"]}
                      width={"50%"}
                      title={t("leaderBoard.levelCard.header2")}
                      color="#00C3FD"
                      icon={Images.listen}
                      path="/app/LifetimeCount/Listens/curator"
                    />
                  ) : null}
                </Stack>
                <Stack gap="35px" direction="row">
                  {data["subscribeMonthly"] ? (
                    <LevelCard
                      data={data["subscribeMonthly"]}
                      width={"100%"}
                      title={t("leaderBoard.levelCard.header3")}
                      color="#FF1CBE"
                      icon={Images.user_add}
                      path="/app/LifetimeCount/SubscribeMonthly/curator"
                    />
                  ) : null}
                </Stack>
                <Stack spacing={1}>
                  <Typography className="fs-14">NFT</Typography>
                  <Stack gap="35px" direction="row">
                    {data["nftOwned"] ? (
                      <LevelCard
                        // isAmount={true}
                        data={data["nftOwned"]}
                        width={"50%"}
                        title={t("leaderBoard.levelCard.header4")}
                        color="#F48706"
                        icon={Images.nft_owned}
                        path="/app/LifetimeCount/NftOwned/curator"
                      />
                    ) : null}
                    {/* Commented as per Requirement */}
                    {/* {data["nftBuy"] ? (
                      <LevelCard
                        data={data["nftBuy"]}
                        width={"25%"}
                        title={"NFT Purchased"}
                        color="#FFEE20"
                        icon={Images.nft_buy}
                        path="/app/LifetimeCount/NftBuy"
                      />
                    ) : null} */}
                    {data["nftMoneyMade"] ? (
                      <LevelCard
                        data={data["nftMoneyMade"]}
                        width={"50%"}
                        title={t("leaderBoard.levelCard.header5")}
                        color="#62FFE1"
                        icon={Images.nft_amount}
                      />
                    ) : null}
                  </Stack>
                </Stack>
                <Stack>
                  <Typography className="fs-14">
                    {t("assetPopup.section2")}
                  </Typography>
                  <Stack gap="35px" direction="row">
                    {data["playlistCreated"] ? (
                      <LevelCard
                        // isAmount={true}
                        data={data["playlistCreated"]}
                        width={"50%"}
                        title={t("leaderBoard.levelCard.header6")}
                        color="#F48706"
                        icon={Images.playlist_add}
                        path="/app/LifetimeCount/PlaylistCreated/curator"
                      />
                    ) : null}
                    {data["playlistStreams"] ? (
                      <LevelCard
                        data={data["playlistStreams"]}
                        width={"50%"}
                        title={t("leaderBoard.levelCard.header7")}
                        color="#00C3FD"
                        icon={Images.playlist}
                        path="/app/LifetimeCount/PlaylistStreams/curator"
                      />
                    ) : null}
                  </Stack>
                </Stack>
                {/* Live Stream Stats Section - HIDDEN */}
                {false && data?.liveStreamStats && (
                  <Stack spacing={1} sx={{ mt: 3 }}>
                    <Typography className="fs-14">Live Streaming</Typography>
                    <Stack gap="35px" direction="row">
                      {data.liveStreamStats.qualifiedStreamsHosted !== undefined ? (
                        <Box className="__level_widget" sx={{ width: "50%" }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap="10px">
                              <Typography>🎥</Typography>
                              <Typography>Streams Hosted</Typography>
                            </Stack>
                          </Stack>
                          <Typography className="faded ml_auto fs-30">
                            {data.liveStreamStats.qualifiedStreamsHosted}
                          </Typography>
                        </Box>
                      ) : null}
                      {data.liveStreamStats.uniqueViewers !== undefined ? (
                        <Box className="__level_widget" sx={{ width: "50%" }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap="10px">
                              <Typography>👁️</Typography>
                              <Typography>Unique Viewers</Typography>
                            </Stack>
                          </Stack>
                          <Typography className="faded ml_auto fs-30">
                            {kFormatter(data.liveStreamStats.uniqueViewers)}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                    <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                      {data.liveStreamStats.totalHostingMinutes !== undefined ? (
                        <Box className="__level_widget" sx={{ width: "50%" }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap="10px">
                              <Typography>⏱️</Typography>
                              <Typography>Stream Minutes</Typography>
                            </Stack>
                          </Stack>
                          <Typography className="faded ml_auto fs-30">
                            {kFormatter(data.liveStreamStats.totalHostingMinutes)}
                          </Typography>
                        </Box>
                      ) : null}
                      {data.liveStreamStats.songsPlayed !== undefined ? (
                        <Box className="__level_widget" sx={{ width: "50%" }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap="10px">
                              <Typography>🎵</Typography>
                              <Typography>Songs Played</Typography>
                            </Stack>
                          </Stack>
                          <Typography className="faded ml_auto fs-30">
                            {kFormatter(data.liveStreamStats.songsPlayed)}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>
                )}
                </>
                ) : null}
                
                {isPremium && data.length === 0 ? (
                  <NoData lable={t("noData.noData")} />
                ) : null}
                
                {/* Premium Subscription Button */}
                {!isPremium && (
                  <Box sx={{ mt: 4, mb: 2, textAlign: "center" }}>
                    <Typography sx={{ mb: 2, color: "#888" }}>
                      Unlock detailed statistics and track your progress with Premium
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setShowSubscriptionPopup(true)}
                      sx={{
                        background: "linear-gradient(45deg, #2CF488 30%, #FFD700 90%)",
                        color: "#000",
                        fontWeight: "bold",
                        px: 4,
                        py: 1.5,
                        "&:hover": {
                          background: "linear-gradient(45deg, #2CF488 60%, #FFD700 100%)",
                        }
                      }}
                    >
                      Upgrade to Premium
                    </Button>
                  </Box>
                )}
              </Box>
            </Stack>
          </TabPanel>
          <TabPanel value={value} index={1} dir={theme.direction}>
            <Stack>
              <Box sx={{ py: [4, 3, 1], textAlign: "center", position: "relative" }}>
                <Stack>
                  <Typography
                    sx={{
                      fontSize: ["48px !important","60px !important", "96px !important"],
                      lineHeight: "1 !important",
                    }}
                  >
                    {isPremium ? (creatorData?.overallLevel || creatorData?.avgLevel || avgLevel || 1) : "?"}
                  </Typography>
                  <Typography
                    className="fs-20"
                    sx={{ color: "#2CF488", letterSpacing: ["12px !important", "24px !important"], }}
                  >
                    CREATOR LEVEL
                  </Typography>
                </Stack>
              </Box>
              <Box
                className="__main_pading"
                sx={{ position: "relative", paddingBottom: "100px" }}
              >
                {isPremium ? (
                  <>
                <Stack gap="35px" direction="row">
                  {creatorData["songReleased"] ? (
                    <LevelCard
                      data={creatorData["songReleased"]}
                      width={"50%"}
                      title={t("leaderBoard.levelCard.header8")}
                      color="#00C3FD"
                      icon={Images.listen}
                      path="/app/LifetimeCount/SongReleased/creator"
                    />
                  ) : null}
                  {creatorData["nftSellAmount"] ? (
                    <LevelCard
                      data={creatorData["nftSellAmount"]}
                      width={"50%"}
                      isAmount={true}
                      title={t("leaderBoard.levelCard.header9")}
                      color="#2CF488"
                      icon={Images.nft_amount}
                      path="/app/LifetimeCount/NftSold/creator"
                    />
                  ) : null}
                </Stack>
                <Stack gap="35px" direction="row" marginTop="30px">
                  {(creatorData["playlistAdd"] || creatorData["playlistAdds"]) ? (
                    <LevelCard
                      data={creatorData["playlistAdd"] || creatorData["playlistAdds"]}
                      width={"50%"}
                      title={t("leaderBoard.levelCard.header10")}
                      color="#FFEE20"
                      icon={Images.playlist_add}
                      path="/app/LifetimeCount/PlaylistAdd/creator"
                    />
                  ) : null}
                  {creatorData["streamRevenue"] ? (
                    <LevelCard
                      data={creatorData["streamRevenue"]}
                      width={"50%"}
                      isAmount={true}
                      title={t("leaderBoard.levelCard.header11")}
                      color="#00C3FD"
                      icon={Images.nft_amount}
                      path="/app/LifetimeCount/StreamRevenue/creator"
                    />
                  ) : null}
                </Stack>
                
                {/* Live Streaming Stats for Creator - HIDDEN */}
                {false && <Stack spacing={1} sx={{ mt: 3 }}>
                  <Typography className="fs-14">Live Streaming Stats</Typography>
                  
                  {/* Primary Live Stream Stats */}
                  <Stack gap="35px" direction="row">
                    {(creatorData["liveStreamHosting"] || creatorData?.liveStream) ? (
                      <LevelCard
                        data={{
                          count: creatorData?.liveStream?.qualifiedStreamsHosted || creatorData["liveStreamHosting"]?.count || 0,
                          level: Math.floor((creatorData?.liveStream?.qualifiedStreamsHosted || 0) / 5) + 1,
                          upperLimit: 500
                        }}
                        width={"50%"}
                        title="Qualified Streams"
                        color="#9C27B0"
                        icon="✅"
                        path="/app/LifetimeCount/QualifiedStreams/creator"
                      />
                    ) : null}
                    {creatorData?.liveStream ? (
                      <LevelCard
                        data={{
                          count: creatorData.liveStream.totalStreamsHosted || 0,
                          level: Math.floor((creatorData.liveStream.totalStreamsHosted || 0) / 10) + 1,
                          upperLimit: 1000
                        }}
                        width={"50%"}
                        title="Total Streams"
                        color="#3F51B5"
                        icon="🎥"
                        path="/app/LifetimeCount/TotalStreams/creator"
                      />
                    ) : null}
                  </Stack>
                  
                  {/* Stream Time Stats */}
                  <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>⏱️</Typography>
                          <Typography>Total Minutes</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {kFormatter(creatorData?.liveStream?.totalHostingMinutes || creatorData?.liveStreamHosting?.totalMinutes || 0)}
                      </Typography>
                      <Typography className="fs-12" sx={{ color: '#888' }}>
                        Level {Math.floor((creatorData?.liveStream?.totalHostingMinutes || creatorData?.liveStreamHosting?.totalMinutes || 0) / 60) + 1}
                      </Typography>
                    </Box>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>⏳</Typography>
                          <Typography>Avg Duration</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {creatorData?.liveStream?.averageStreamDuration || 0} min
                      </Typography>
                    </Box>
                  </Stack>
                  
                  {/* Viewer Stats */}
                  <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>👁️</Typography>
                          <Typography>Total Viewers</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {kFormatter(creatorData?.liveStream?.totalViewers || 0)}
                      </Typography>
                    </Box>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>👤</Typography>
                          <Typography>Unique Viewers</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {kFormatter(creatorData?.liveStream?.uniqueViewers || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  {/* Songs and Performance Stats */}
                  <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>🎵</Typography>
                          <Typography>Songs Played</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {kFormatter(creatorData?.liveStream?.songsPlayed || 0)}
                      </Typography>
                    </Box>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>📊</Typography>
                          <Typography>Avg Viewers</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {creatorData?.liveStream?.averageViewersPerStream || 0}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  {/* Additional Stats */}
                  <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>🏆</Typography>
                          <Typography>Longest Stream</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-30">
                        {Math.floor((creatorData?.liveStream?.longestStreamMinutes || 0) / 60)}h {(creatorData?.liveStream?.longestStreamMinutes || 0) % 60}m
                      </Typography>
                    </Box>
                    <Box className="__level_widget" sx={{ width: "50%" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="10px">
                          <Typography>📅</Typography>
                          <Typography>Last Stream</Typography>
                        </Stack>
                      </Stack>
                      <Typography className="faded ml_auto fs-16">
                        {creatorData?.liveStream?.mostRecentStreamDate 
                          ? new Date(creatorData.liveStream.mostRecentStreamDate).toLocaleDateString()
                          : 'Never'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>}
                
                {/* Other Creator Stats */}
                <Stack spacing={1} sx={{ mt: 3 }}>
                  <Typography className="fs-14">Creator Performance</Typography>
                  <Stack gap="35px" direction="row">
                    {creatorData["songStreams"] ? (
                      <LevelCard
                        data={creatorData["songStreams"]}
                        width={"50%"}
                        title="Song Plays"
                        color="#E91E63"
                        icon="/images/Mask Group 44.svg"
                        path="/app/LifetimeCount/SongStreams/creator"
                      />
                    ) : null}
                    {creatorData["followers"] ? (
                      <LevelCard
                        data={creatorData["followers"]}
                        width={"50%"}
                        title="Followers"
                        color="#FF5722"
                        icon="/images/user_add_line.svg"
                        path="/app/LifetimeCount/Followers/creator"
                      />
                    ) : null}
                  </Stack>
                  <Stack gap="35px" direction="row" sx={{ mt: 2 }}>
                    {creatorData["tipsReceived"] ? (
                      <LevelCard
                        data={creatorData["tipsReceived"]}
                        width={"50%"}
                        isAmount={true}
                        title="Tips Received"
                        color="#4CAF50"
                        icon="/images/hand-holding-heart-solid.svg"
                        path="/app/LifetimeCount/TipsReceived/creator"
                      />
                    ) : null}
                  </Stack>
                </Stack>
                
                {/* Level Breakdown for Debug */}
                {creatorData?.levelBreakdown && (
                  <Stack spacing={1} sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                    <Typography className="fs-12" sx={{ color: '#888' }}>Level Breakdown (Debug)</Typography>
                    <Stack direction="row" gap={2} flexWrap="wrap">
                      {Object.entries(creatorData.levelBreakdown).map(([key, level]) => (
                        <Typography key={key} className="fs-10" sx={{ color: '#aaa' }}>
                          {key}: L{level}
                        </Typography>
                      ))}
                    </Stack>
                    <Typography className="fs-10" sx={{ color: '#aaa' }}>
                      Total Points: {creatorData.totalPoints || 0}
                    </Typography>
                  </Stack>
                )}
                
                </>
                ) : null}
                
                {isPremium && data.length === 0 ? (
                  <NoData lable={t("noData.noData")} />
                ) : null}
                
                {/* Premium Subscription Button */}
                {!isPremium && (
                  <Box sx={{ mt: 4, mb: 2, textAlign: "center" }}>
                    <Typography sx={{ mb: 2, color: "#888" }}>
                      Unlock detailed creator statistics and track your progress with Premium
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setShowSubscriptionPopup(true)}
                      sx={{
                        background: "linear-gradient(45deg, #2CF488 30%, #FFD700 90%)",
                        color: "#000",
                        fontWeight: "bold",
                        px: 4,
                        py: 1.5,
                        "&:hover": {
                          background: "linear-gradient(45deg, #2CF488 60%, #FFD700 100%)",
                        }
                      }}
                    >
                      Upgrade to Premium
                    </Button>
                  </Box>
                )}
              </Box>
            </Stack>
          </TabPanel>
        </Box>
        {loading ? <Loader /> : null}
      </Box>
      <SubscriptionPopup 
        isOpen={showSubscriptionPopup} 
        onClose={() => setShowSubscriptionPopup(false)} 
      />
    </Box>
  );
}
